/**
 * Estado y builders del formulario de Cargador (US-2.2, TG-125).
 *
 * Compartido por el wizard de alta (`StationForm`) y la página (`CreateStationPage`), que
 * usan estas funciones para componer el POST del contrato. Las reglas de validación viven
 * en los grupos de campos (`StationIdentityFields`, `StationConnectorsFields`).
 *
 * La identidad visible del cargador es **`Ubicación · ChargePoint ID`**: NO existe campo
 * `name` en el contrato; el índice `id` del formulario es el ChargePoint ID.
 */
import type {
  ConnectorInput,
  ConnectorStandard,
  CreateConnectorBody,
  CreateStationBody,
  OcppVersion,
  PowerType,
  StationStatus,
  UpdateConnectorBody,
  UpdateStationBody,
} from '@goelectric/shared-types';

/** Tope de ubicaciones traídas para el selector (mismo criterio que el resto de selectores). */
export const LOCATION_OPTIONS_LIMIT = 50;

/** Fila del editor de conectores (`Form.List`). Todo opcional salvo lo que el contrato exige. */
export interface ConnectorFormRow {
  /** Nº de conector OCPP (entero ≥ 1, único dentro del cargador). */
  id?: number;
  standard?: ConnectorStandard;
  powerType?: PowerType;
  maxPowerKw?: number;
  maxVoltageV?: number;
  maxAmperageA?: number;
}

/** Forma plana del formulario. `locationId` es el id de la Ubicación seleccionada. */
export interface StationFormState {
  /** ChargePoint ID (único por tenant). */
  id: string;
  vendor?: string;
  model?: string;
  protocol: OcppVersion;
  maxPowerKw: number;
  locationId: string;
  connectors: ConnectorFormRow[];
}

/**
 * Forma del formulario de EDICIÓN (PATCH). Reusa los grupos de campos del alta, pero
 * `id` y `protocol` van **deshabilitados** (inmutables por contrato) y se agrega el
 * `status` (ciclo de vida). NO hay `connectors`: el PATCH no los acepta.
 */
export interface StationEditFormState {
  /** ChargePoint ID (inmutable; sólo se muestra). */
  id: string;
  /** Protocolo OCPP (inmutable; sólo se muestra). */
  protocol: OcppVersion;
  vendor?: string;
  model?: string;
  maxPowerKw: number;
  locationId: string;
  status: StationStatus;
}

/** `''`/espacios → `undefined`, para no mandar campos vacíos. */
function trimOrUndefined(value?: string): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/** Mapea una fila del editor al `ConnectorInput` del contrato; `null` si falta lo requerido. */
function buildConnectorInput(row: ConnectorFormRow): ConnectorInput | null {
  if (typeof row.id !== 'number' || !row.standard || typeof row.maxPowerKw !== 'number') {
    return null;
  }
  const connector: ConnectorInput = {
    id: row.id,
    standard: row.standard,
    maxPowerKw: row.maxPowerKw,
  };
  if (row.powerType) connector.powerType = row.powerType;
  if (typeof row.maxVoltageV === 'number') connector.maxVoltageV = row.maxVoltageV;
  if (typeof row.maxAmperageA === 'number') connector.maxAmperageA = row.maxAmperageA;
  return connector;
}

/**
 * Cuerpo del POST. La validación del Form garantiza los requeridos; si faltara algo
 * devuelve `null` (defensivo). `connectors` (min 1) es requerido por el contrato: si queda
 * vacío o con una fila incompleta, también devuelve `null`.
 */
export function buildCreateStationBody(values: StationFormState): CreateStationBody | null {
  const id = trimOrUndefined(values.id);
  const locationId = trimOrUndefined(values.locationId);
  if (!id || !locationId || typeof values.maxPowerKw !== 'number' || !values.protocol) {
    return null;
  }

  const connectors: ConnectorInput[] = [];
  for (const row of values.connectors ?? []) {
    const connector = buildConnectorInput(row);
    if (!connector) {
      return null;
    }
    connectors.push(connector);
  }
  if (connectors.length < 1) {
    return null;
  }

  const body: CreateStationBody = {
    id,
    locationId,
    protocol: values.protocol,
    maxPowerKw: values.maxPowerKw,
    connectors,
  };
  const vendor = trimOrUndefined(values.vendor);
  const model = trimOrUndefined(values.model);
  if (vendor) body.vendor = vendor;
  if (model) body.model = model;
  return body;
}

/**
 * Cuerpo del PATCH parcial del CARGADOR. `id`/`protocol` no viajan (inmutables por
 * contrato). `locationId` sólo se manda si CAMBIÓ respecto de la original: el backend
 * revalida la locación cuando viene en el body y una locación que quedó `INACTIVE` haría
 * fallar la edición de un campo ajeno a la ubicación.
 */
export function buildUpdateStationBody(
  values: StationEditFormState,
  originalLocationId: string,
): UpdateStationBody {
  const body: UpdateStationBody = {
    vendor: values.vendor?.trim() ?? '',
    model: values.model?.trim() ?? '',
    maxPowerKw: values.maxPowerKw,
    status: values.status,
  };
  if (values.locationId && values.locationId !== originalLocationId) {
    body.locationId = values.locationId;
  }
  return body;
}

/**
 * Cuerpo del POST de un conector (sub-recurso). Mismo shape que `buildConnectorInput`:
 * `id`/`standard`/`maxPowerKw` son requeridos. `null` si falta lo requerido (defensivo).
 */
export function buildCreateConnectorBody(row: ConnectorFormRow): CreateConnectorBody | null {
  return buildConnectorInput(row);
}

/**
 * Cuerpo del PATCH parcial de un conector. NO incluye `id` (el contrato no lo acepta:
 * cambiarlo no re-sincroniza el core). Sólo los campos con valor.
 */
export function buildUpdateConnectorBody(row: ConnectorFormRow): UpdateConnectorBody | null {
  if (!row.standard || typeof row.maxPowerKw !== 'number') {
    return null;
  }
  const body: UpdateConnectorBody = { standard: row.standard, maxPowerKw: row.maxPowerKw };
  if (row.powerType) body.powerType = row.powerType;
  if (typeof row.maxVoltageV === 'number') body.maxVoltageV = row.maxVoltageV;
  if (typeof row.maxAmperageA === 'number') body.maxAmperageA = row.maxAmperageA;
  return body;
}

/**
 * Regla antd de coherencia de potencia: un conector no puede superar la potencia del
 * gabinete. `getCabinet` se evalúa al validar (en el editor del alta el gabinete cambia en
 * vivo); en el modal de conectores devuelve la potencia fija del cargador.
 */
export function connectorPowerRule(getCabinet: () => number | undefined) {
  return {
    validator: (_rule: unknown, value: number | undefined): Promise<void> => {
      const cabinet = getCabinet();
      return typeof value === 'number' && typeof cabinet === 'number' && value > cabinet
        ? Promise.reject(
            new Error(`La potencia del conector no puede superar la del gabinete (${cabinet} kW).`),
          )
        : Promise.resolve();
    },
  };
}

/**
 * Regla antd de número de conector único: `getTaken` se evalúa al validar y devuelve los
 * números ya usados (excluyendo el actual). Compartida por el editor del alta y el modal.
 */
export function connectorUniqueIdRule(getTaken: () => (number | undefined)[]) {
  return {
    validator: (_rule: unknown, value: number | undefined): Promise<void> => {
      const taken = getTaken();
      return value != null && taken.includes(value)
        ? Promise.reject(new Error('Ya hay un conector con ese número.'))
        : Promise.resolve();
    },
  };
}
